'use client';
import React, {
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';

import { getLogger } from '@lib/logging';
import { useAnalytics } from '../../useAnalytics';
import { useAnalyticsContext } from '../../useAnalyticsContext';
import { useConstant } from '../../hooks/useConstant';
import {
  SegmentPageViewContext,
  SegmentPageViewContextValue,
} from './segment-page-view-context';

export { SegmentPageViewContext };
export type { SegmentPageViewContextValue };

const segmentLogger = getLogger('SegmentPage');

export interface SegmentPageViewProps {
  additionalProps?: Record<string, any>;
  name?: string;
  children?: ReactNode;
}

export const SegmentPageView: React.FC<SegmentPageViewProps> = ({
  additionalProps,
  name = '',
  children,
}: PropsWithChildren<SegmentPageViewProps>) => {
  const { segmentPage } = useAnalytics();
  const { pageViewId: previousPageViewId } = useAnalyticsContext();
  const [pageViewReady, setPageViewReady] = useState(false);
  const staticPreviousPageViewId = useRef(previousPageViewId);
  const pageViewId = useConstant(() => uuid());
  useEffect(() => {
    segmentPage(name, {
      pageview_id: pageViewId,
      ...additionalProps,
    });
    setPageViewReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <SegmentPageViewContext.Provider
      value={{
        pageName: name,
        pageViewId,
        pageViewReady,
        previousPageViewId: staticPreviousPageViewId.current,
      }}
    >
      {children}
    </SegmentPageViewContext.Provider>
  );
};

export const useSegmentPageView = (
  segmentPageAttributes?: SegmentPageViewContextValue,
): SegmentPageViewContextValue => {
  const context = useContext(SegmentPageViewContext);
  if (!context) {
    if (segmentPageAttributes) return segmentPageAttributes;
    const errMsg = 'useSegmentPageView must be used within a SegmentPageView';
    segmentLogger.error(errMsg);
    throw new Error(errMsg);
  }
  return context;
};
